import React from "react";
import { Alert, Divider, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";

const { Text } = Typography;

import { commonLabels } from "./fieldLabels";
import { formSections } from "./sections";
import { InputItem, StateItem, PhoneNumberItem, RadioItem, EmailItem } from "../form";
import { zipInputRules } from "../../utils";
import DobFormItem from "../DobFormItem";
import { GenderFormItem } from "../../GenderFormItem";
import { insuranceFields, patientFields, practiceFields } from "./fieldNames";
import { FormDividerText } from "../styledComponents";
import { BLUE_3, GRAY_500, LIGHT_BLUE_5 } from "../../../../../constant/colors";
import { useGetLabels } from "../../../../../hooks/useGetLabels";
import { PhysicanItem } from "../form/PhysicianItem";

export const CommonFieldBankController = () => {
  const { getLabelsBySection } = useGetLabels(commonLabels);
  const insuranceLabels = getLabelsBySection(formSections.INSURANCE_SECTION);
  const patientLabels = getLabelsBySection(formSections.PATIENT_SECTION);
  const practiceLabels = getLabelsBySection(formSections.PRACTICE_SECTION);
  return {
    patient: {
      gender: ({
        name = patientFields.gender,
        label = patientLabels.gender,
        showError,
        thirdOption = "None",
        width = "100%",
        radioStyle = { display: "flex" },
        additionalOptions,
        showThirdOption = true,
        widthForAdditionalOptions,
        minWidth,
        minWidthForAdditionalOptions,
        required,
        key = "patient-gender"
      }) => (
        <GenderFormItem
          required={required}
          formItemName={name}
          label={label}
          showError={showError}
          width={width}
          thirdOption={thirdOption}
          radioStyle={radioStyle}
          additionalOptions={additionalOptions}
          showThirdOption={showThirdOption}
          widthForAdditionalOptions={widthForAdditionalOptions}
          minWidth={minWidth}
          minWidthForAdditionalOptions={minWidthForAdditionalOptions}
          key={key}
        />
      ),
      dob: ({ required, name = patientFields.dob, label = patientLabels.dob, disabled, key = "patient-dob" }) => (
        <DobFormItem required={required} name={name} label={label} disabled={disabled} key={key} />
      ),
      additional_phone_number: ({
        required,
        name = patientFields.additionalPhone,
        label = patientLabels.additionalPhoneNumber,
        placeholder = patientLabels.additionalPhoneNumberPlaceholder,
        typeName = patientFields.additionalPhoneType,
        key = "patient-additiona-number"
      }) => (
        <PhoneNumberItem
          required={required}
          name={name}
          label={label}
          placeholder={placeholder}
          typeName={typeName}
          key={key}
        />
      )
    },
    insurance: {
      isInsured: ({
        required,
        name = insuranceFields.isInsured,
        label = insuranceLabels.isInsured,
        formItemProps,
        key = "insurance-is-insured"
      }) => (
        <RadioItem
          key={key}
          formItemProps={formItemProps}
          name={name}
          label={label}
          rules={[{ required }]}
          radioOptions={[
            { value: true, displayValue: "Yes" },
            { value: false, displayValue: "No" }
          ]}
        />
      ),
      payerName: ({
        required,
        name = insuranceFields.primaryPayerName,
        label = insuranceLabels.primaryPayerName,
        inputPlaceholder = insuranceLabels.payerNamePlaceholder,
        key = "insurance-payer-name"
      }) => (
        <InputItem name={name} label={label} rules={[{ required }]} inputPlaceholder={inputPlaceholder} key={key} />
      ),
      informationSection: ({ isInsured, key = "insurance-information-section" }) => (
        <Alert
          style={{ backgroundColor: LIGHT_BLUE_5, borderRadius: 8, marginBottom: 20 }}
          message={
            <Text style={{ color: BLUE_3, whiteSpace: "pre-line" }}>
              {isInsured ? insuranceLabels.insuredAlreadyChosen : insuranceLabels.uninsuredAlreadyChosen}
            </Text>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined style={{ alignSelf: "flex-start", paddingTop: 4 }} />}
          key={key}
        />
      )
    },
    practice: {
      prescriberFirstName: ({
        required,
        name = practiceFields.prescriberFirstName,
        label = practiceLabels.prescriberFirstName,
        inputPlaceholder = practiceLabels.prescriberFirstNamePlaceholder,
        key = "prescriber-first-name"
      }) => (
        <InputItem name={name} label={label} rules={[{ required }]} inputPlaceholder={inputPlaceholder} key={key} />
      ),
      prescriberLastName: ({
        required,
        name = practiceFields.prescriberLastName,
        label = practiceLabels.prescriberLastName,
        inputPlaceholder = practiceLabels.prescriberLastNamePlaceholder,
        key = "prescriber-last-name"
      }) => (
        <InputItem name={name} label={label} rules={[{ required }]} inputPlaceholder={inputPlaceholder} key={key} />
      ),
      prescriberSelect: ({
        name = practiceFields.prescriberName,
        label = practiceLabels.prescriberName,
        required,
        dropdownRender,
        onClear,
        onChange,
        isOpen,
        onDropdownVisibleChange,
        placeholder = practiceLabels.prescriberFullNamePlaceholder,
        id = "EE_genentech_free_drug_physician_select",
        key = "prescriber-select"
      }) => (
        <PhysicanItem
          name={name}
          label={label}
          required={required}
          dropdownRender={dropdownRender}
          onClear={onClear}
          onChange={onChange}
          isOpen={isOpen}
          onDropdownVisibleChange={onDropdownVisibleChange}
          placeholder={placeholder}
          id={id}
          key={key}
        />
      )
    },
    common: {
      id: () => <InputItem name={patientFields.id} formItemProps={{ hidden: true }} />,
      firstName: ({
        required,
        name = patientFields.firstName,
        label = patientLabels.firstName,
        inputPlaceholder = patientLabels.firstNamePlaceholder,
        inputProps,
        rules = [],
        key = "patient-first-name"
      }) => (
        <InputItem
          name={name}
          label={label}
          rules={[{ required }, ...rules]}
          inputPlaceholder={inputPlaceholder}
          inputProps={inputProps}
          key={key}
        />
      ),
      lastName: ({
        required,
        name = patientFields.lastName,
        label = patientLabels.lastName,
        inputPlaceholder = patientLabels.lastNamePlaceholder,
        inputProps,
        key = "patient-last-name",
        rules = []
      }) => (
        <InputItem
          name={name}
          label={label}
          rules={[{ required }, ...rules]}
          inputPlaceholder={inputPlaceholder}
          inputProps={inputProps}
          key={key}
        />
      ),
      phone_number: ({
        required,
        name = patientFields.phone,
        label = patientLabels.phoneNumber,
        placeholder = patientLabels.phoneNumberPlaceholder,
        typeName = patientFields.phoneType,
        addon = true,
        key = "patient-phone-number"
      }) => (
        <PhoneNumberItem
          required={required}
          name={name}
          label={label}
          placeholder={placeholder}
          typeName={typeName}
          addon={addon}
          key={key}
        />
      ),
      address: ({
        required,
        name = patientFields.address,
        label = patientLabels.address,
        inputPlaceholder = patientLabels.addressPlaceholder,
        rules = [],
        key = "patient-address"
      }) => (
        <InputItem
          name={name}
          label={label}
          inputPlaceholder={inputPlaceholder}
          rules={[{ required }, ...rules]}
          key={key}
        />
      ),
      city: ({
        required,
        name = patientFields.city,
        label = patientLabels.city,
        inputPlaceholder = patientLabels.cityPlaceholder,
        rules = [],
        key = "patient-city"
      }) => (
        <InputItem
          name={name}
          label={label}
          inputPlaceholder={inputPlaceholder}
          rules={[{ required }, ...rules]}
          key={key}
        />
      ),
      npi: ({
        required,
        name = practiceFields.prescriberNpi,
        label = practiceLabels.prescriberNpi,
        inputPlaceholder = practiceLabels.npiPlaceholder,
        key = "prescriber-npi"
      }) => (
        <InputItem
          name={name}
          label={label}
          rules={[{ required }, { min: 10, message: "" }, { max: 10, message: "" }]}
          inputPlaceholder={inputPlaceholder}
          inputProps={{ maxLength: 10 }}
          key={key}
        />
      ),
      groupTaxId: ({
        required,
        name = practiceFields.groupTaxId,
        label = practiceLabels.groupTaxId,
        inputPlaceholder = practiceLabels.groupTaxIdPlaceholder,
        key = "group-tax-id"
      }) => (
        <InputItem name={name} label={label} rules={[{ required }]} inputPlaceholder={inputPlaceholder} key={key} />
      ),
      state: ({
        required,
        name = patientFields.state,
        label = patientLabels.state,
        placeholder = patientLabels.statePlaceholder,
        key = "patient-state"
      }) => <StateItem rules={[{ required }]} name={name} label={label} placeholder={placeholder} key={key} />,
      zip_code: ({
        required,
        name = patientFields.zip,
        label = patientLabels.zipCode,
        inputPlaceholder = patientLabels.zipCodePlaceholder,
        key = "patient-zip-code"
      }) => (
        <InputItem
          rules={[{ required }, ...zipInputRules]}
          name={name}
          label={label}
          inputPlaceholder={inputPlaceholder}
          inputProps={{ maxLength: 5, style: { width: "100%" } }}
          key={key}
        />
      ),
      email: ({
        required,
        name = patientFields.email,
        label = patientLabels.email,
        placeholder = patientLabels.emailPlaceholder,
        inputProps = {},
        additionalRules = [],
        key = "patient-email"
      }) => (
        <EmailItem
          required={required}
          name={name}
          label={label}
          placeholder={placeholder}
          inputProps={inputProps}
          additionalRules={additionalRules}
          key={key}
        />
      ),
      divider: ({ id, text }) => {
        return (
          <Divider id={id} orientation="left" orientationMargin="0" style={{ color: GRAY_500 }}>
            <FormDividerText>{text}</FormDividerText>
          </Divider>
        );
      }
    }
  };
};
